import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { DictionaryEntry, DictionaryUsage } from '../../types';

export default function DictionaryEntry() {
  const { id } = useParams<{ id: string }>();
  const [entry, setEntry] = useState<DictionaryEntry | null>(null);
  const [usages, setUsages] = useState<DictionaryUsage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEntry = async () => {
      const { data: entryData } = await supabase
        .from('dictionary_entries')
        .select('*')
        .eq('id', id)
        .single();
      setEntry(entryData);

      const { data: usagesData } = await supabase
        .from('dictionary_usages')
        .select('*')
        .eq('entry_id', id);
      setUsages(usagesData || []);
      setLoading(false);
    };
    fetchEntry();
  }, [id]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto"></div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="text-center py-8">
        <p className="text-[#8E8E93]">Entrada no encontrada</p>
        <Link to="/dictionary" className="text-primary mt-2 block">Volver al diccionario</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link to="/dictionary" className="text-primary">← Volver</Link>
      
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs bg-[#3A3A3C] px-2 py-1 rounded">#{entry.strong_number}</span>
          <span className={`text-xs px-2 py-1 rounded ${
            entry.language === 'greek' ? 'bg-blue-100 text-blue-700' :
            entry.language === 'hebrew' ? 'bg-purple-100 text-purple-700' :
            'bg-orange-100 text-orange-700'
          }`}>
            {entry.language === 'greek' ? 'Griego' : entry.language === 'hebrew' ? 'Hebreo' : 'Arameo'}
          </span>
        </div>

        <h1 className="text-3xl font-bold text-white mb-1">{entry.word}</h1>
        
        {entry.transliteration && (
          <p className="text-lg text-[#8E8E93] italic mb-4">{entry.transliteration}</p>
        )}

        {entry.pronunciation && (
          <p className="text-sm text-[#8E8E93] mb-4">Pronunciación: {entry.pronunciation}</p>
        )}

        {entry.part_of_speech && (
          <p className="text-sm bg-[#3A3A3C] inline-block px-3 py-1 rounded-full mb-4">
            {entry.part_of_speech}
          </p>
        )}

        <div className="mt-4">
          <h2 className="font-semibold text-[#E5E5EA] mb-2">Definición</h2>
          <p className="text-[#B3B3B3]">{entry.definition}</p>
        </div>

        {entry.usage && (
          <div className="mt-4">
            <h2 className="font-semibold text-[#E5E5EA] mb-2">Uso</h2>
            <p className="text-[#B3B3B3]">{entry.usage}</p>
          </div>
        )}
      </div>

      {usages.length > 0 && (
        <div className="card p-4">
          <h2 className="font-semibold text-[#E5E5EA] mb-3">Usos en la Biblia</h2>
          <div className="space-y-2">
            {usages.map((usage) => (
              <div key={usage.id} className="p-3 bg-[#2C2C2E] rounded-lg">
                <p className="text-primary font-medium">
                  {usage.book} {usage.chapter}:{usage.verse}
                </p>
                <p className="text-sm text-[#B3B3B3] mt-1">{usage.context}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
